import { useMap } from 'react-map-gl/mapbox';
import { proxy, useSnapshot } from 'valtio';
import type { ObjectFile } from '~/common/types';
import type { LngLat, Point } from '~/components2/Map/canvas/SurfaceBase';

export const OFFSET = 20;

export interface ImageHoverState {
  file: ObjectFile | undefined;
  point: Point;
  lngLat: LngLat;
  setParams: (f: ObjectFile, p: Point, l: LngLat) => void;
  reset: () => void;
}

export const imageHoverState = proxy<ImageHoverState>({
  file: undefined,
  point: { x: NaN, y: NaN },
  lngLat: { lng: NaN, lat: NaN },
  setParams: (f: ObjectFile, p: Point, l: LngLat) => {
    imageHoverState.file = f;
    imageHoverState.point = p;
    imageHoverState.lngLat = l;
  },
  reset: () => {
    imageHoverState.file = undefined;
    imageHoverState.point = { x: NaN, y: NaN };
    imageHoverState.lngLat = { lng: NaN, lat: NaN };
  },
});

export default function ImageHoverPreview() {
  const { file, point, lngLat } = useSnapshot(imageHoverState);
  const { current: map } = useMap();

  if (Number.isNaN(point.x) || Number.isNaN(lngLat.lng) || !map) {
    return null;
  }

  if (!file) {
    return <div className="bg-white">Loading...</div>;
  }

  const getCoords = () => {
    const { x, y } = point;
    const { width, height } = map.getCanvas();
    if (x > width / 2) {
      if (y > height / 2) {
        return {
          right: `${width - x + OFFSET}px`,
          bottom: `${height - y + OFFSET}px`,
        };
      }
      return { right: `${width - x + OFFSET}px`, top: `${y + OFFSET}px` };
    }

    if (y > height / 2) {
      return { left: `${x + OFFSET}px`, bottom: `${height - y + OFFSET}px` };
    }
    return { left: `${x + OFFSET}px`, top: `${y + OFFSET}px` };
  };

  return (
    <div
      className="absolute rounded-sm border border-solid border-secondary/30 bg-white p-1.5 shadow-lg outline-none"
      // dynamic styling does not work well in tailwind, setting inline styles for this.
      style={{ ...getCoords() }}
    >
      <div className="relative max-h-[200px] max-w-[200px]">
        <img src={file.path} alt="Attachment preview" className="max-h-[200px] max-w-[200px] rounded-sm" />
      </div>
    </div>
  );
}
